import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { useSelector } from "react-redux";
import {
	ExpansionPanel,
	ExpansionPanelDetails,
	ExpansionPanelSummary,
	Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { isOrganisationModuleEnabled } from "utils/navigation-filter";

const useStyles = makeStyles((theme) => ({
	root: {
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.white,
		overflow: "hidden",
		boxShadow: "none",
		"&:before": {
			display: "none",
		},
	},
	summary: {
		padding: theme.spacing(0, 1.5, 0, 2),
		minHeight: 52,
		"&.Mui-expanded": {
			minHeight: 52,
		},
		"& .MuiExpansionPanelSummary-content": {
			margin: theme.spacing(1.25, 0),
			alignItems: "center",
		},
	},
	/* Match timesheet list collapsed row primary line (staff name) — theme h6 is 14px */
	summaryTitle: {
		fontWeight: 600,
		fontSize: "1rem",
		color: theme.palette.text.primary,
	},
	countMuted: {
		fontWeight: 500,
		color: theme.palette.text.secondary,
		marginLeft: theme.spacing(0.5),
	},
	expandIcon: {
		color: theme.palette.text.secondary,
	},
	details: {
		padding: 0,
		display: "block",
		overflow: "hidden",
	},
}));

/**
 * Site summary accordion for a health / records section.
 * @param {string|null} countKey — camelCase field on get-site-info-counts; omit for no badge.
 * @param {string} moduleTitle — title passed to isOrganisationModuleEnabled (see navigation-filter).
 */
const SiteRecordSectionPanel = ({
	site,
	organisationId,
	expanded,
	onExpandedChange,
	title,
	countKey,
	moduleTitle,
	sectionId,
	children,
}) => {
	const classes = useStyles();
	const featureFlags = useSelector((state) => state.featureFlags);
	const currentOrganisationFromStore = useSelector(
		({ organisations }) => organisations.currentOrganisation
	);
	const organisationIdResolved = useMemo(() => {
		if (organisationId) {
			return organisationId;
		}
		const co = currentOrganisationFromStore;
		if (typeof co === "string") {
			return co;
		}
		if (co && co.id) {
			return co.id;
		}
		return "";
	}, [organisationId, currentOrganisationFromStore]);

	const [count, setCount] = useState(null);
	const siteId = site?.id;

	const enabled = isOrganisationModuleEnabled(moduleTitle, featureFlags);

	useEffect(() => {
		if (!countKey || !enabled || !organisationIdResolved || !siteId) {
			setCount(null);
			return undefined;
		}
		let cancelled = false;
		safetyWiseApi
			.get(`${organisationIdResolved}/site/${siteId}/get-site-info-counts`)
			.then((res) => {
				if (cancelled) {
					return;
				}
				const raw = res.data?.[countKey];
				if (raw === undefined || raw === null || raw === "") {
					setCount(null);
				} else {
					setCount(raw);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setCount(null);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [countKey, enabled, organisationIdResolved, siteId]);

	useEffect(() => {
		if (!expanded || !countKey || !enabled || !organisationIdResolved || !siteId) {
			return undefined;
		}
		let cancelled = false;
		safetyWiseApi
			.get(`${organisationIdResolved}/site/${siteId}/get-site-info-counts`)
			.then((res) => {
				if (cancelled) {
					return;
				}
				const raw = res.data?.[countKey];
				if (raw === undefined || raw === null || raw === "") {
					setCount(null);
				} else {
					setCount(raw);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setCount(null);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [expanded, countKey, enabled, organisationIdResolved, siteId]);

	if (!enabled || !site || !siteId) {
		return null;
	}

	const countLabel =
		countKey &&
		count !== null &&
		count !== undefined &&
		String(count).length > 0
			? ` (${count})`
			: "";

	return (
		<ExpansionPanel
			className={classes.root}
			elevation={0}
			expanded={expanded}
			onChange={onExpandedChange}
			data-cy={`site-record-section-${sectionId}`}
		>
			<ExpansionPanelSummary
				expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
				className={classes.summary}
				aria-controls={`site-record-section-${sectionId}-content`}
				id={`site-record-section-${sectionId}-header`}
			>
				<Typography
					component="h2"
					className={classes.summaryTitle}
				>
					{title}
					{countLabel !== "" && (
						<span className={classes.countMuted}>{countLabel}</span>
					)}
				</Typography>
			</ExpansionPanelSummary>
			<ExpansionPanelDetails className={classes.details}>
				{expanded && siteId && children}
			</ExpansionPanelDetails>
		</ExpansionPanel>
	);
};

SiteRecordSectionPanel.propTypes = {
	site: PropTypes.object,
	organisationId: PropTypes.string,
	expanded: PropTypes.bool.isRequired,
	onExpandedChange: PropTypes.func.isRequired,
	title: PropTypes.string.isRequired,
	countKey: PropTypes.string,
	moduleTitle: PropTypes.string.isRequired,
	sectionId: PropTypes.string.isRequired,
	children: PropTypes.node,
};

export default SiteRecordSectionPanel;
